import type { Client, SalesDocument } from '@erp/data-access/interfaces';

export interface DocumentCustomerDisplay {
  source: 'local' | 'online' | 'pro';
  sourceLabelKey: string;
  sourceLabel: string;
  name: string;
  secondaryName?: string;
  code?: string | null;
  type: 'company' | 'individual';
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  profilePath?: string | null;
}

export function resolveDocumentCustomer(
  document: SalesDocument | null | undefined,
  clients?: Client[] | null,
): DocumentCustomerDisplay | null {
  if (!document) return null;
  const source = document.customerSource
    ?? (document.proCustomerId || document.proCommandeCode ? 'pro' : document.onlineCustomerId ? 'online' : 'local');

  if (source === 'pro') {
    const electrician = document.proCustomer;
    const finalClient = document.proClient;
    const electricianName = electrician
      ? `${electrician.firstName} ${electrician.lastName}`.trim()
      : null;
    const finalClientName = finalClient
      ? `${finalClient.prenom ?? ''} ${finalClient.nom}`.trim()
      : null;
    return {
      source: 'pro',
      sourceLabelKey: 'sales.customerSource.pro',
      sourceLabel: 'Espace Pro',
      name: finalClientName || electricianName || 'Client Pro',
      secondaryName: finalClientName && electricianName ? `Electricien: ${electricianName}` : undefined,
      code: document.proCommandeCode ?? document.proClientId ?? document.proCustomerId ?? null,
      type: 'individual',
      phone: finalClient?.telephone ?? electrician?.phone ?? electrician?.whatsapp ?? null,
      email: finalClient?.email ?? electrician?.email ?? null,
      address: finalClient?.adresse ?? null,
      city: finalClient?.ville ?? null,
      profilePath: electrician?.id ? `/pro/customers/${electrician.id}` : null,
    };
  }

  if (source === 'online') {
    const customer = document.onlineCustomer;
    const fullName = customer?.fullName
      || [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim();
    return {
      source: 'online',
      sourceLabelKey: 'sales.customerSource.online',
      sourceLabel: 'Boutique en ligne',
      name: fullName || customer?.phone || 'Client boutique en ligne',
      code: document.onlineCustomerId ?? null,
      type: 'individual',
      phone: customer?.phone ?? null,
      email: customer?.email ?? null,
      address: customer?.address ?? null,
      city: customer?.city ?? customer?.primaryCity ?? null,
      profilePath: customer?.id ? `/storefront/customers/${customer.id}` : null,
    };
  }

  const client = document.client ?? clients?.find((c) => c.id === document.clientId);
  if (!client) return null;
  return {
    source: 'local',
    sourceLabelKey: 'sales.customerSource.local',
    sourceLabel: 'Client ERP',
    name: client.name,
    code: client.code,
    type: client.type,
    phone: client.phone,
    email: client.email,
    address: client.address,
    city: client.city,
    profilePath: `/clients/${client.id}`,
  };
}
